import { useState } from 'react';
import { useGetOrganization } from '@/api/auth/organizations/organizations';
import { BuyHoursDialog } from '@/components/support/BuyHoursDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useLanguage } from '@/contexts/LanguageContext';
import { useRoles } from '@/contexts/RolesContext';
import { fillSupportTemplate, formatHours, getDateLocale } from '@/lib/support';

interface ConsultantHoursCardProps {
  orgId: string;
}

/**
 * The organization's Data Wrangler (consultant) hours from Auth: available, used to date and total
 * purchased. Administrators and Billing users can buy more; everyone else is pointed to an administrator.
 */
export function ConsultantHoursCard({ orgId }: ConsultantHoursCardProps) {
  const { t, language } = useLanguage();
  const locale = getDateLocale(language);
  const { isAdmin, isBillingManager } = useRoles();
  const [buying, setBuying] = useState(false);
  const { data, isLoading, error } = useGetOrganization(orgId, { query: { retry: false } });
  const organization = data?.data;
  const canBuy = isAdmin || isBillingManager;

  const hours = (value: number | undefined) =>
    fillSupportTemplate(t.support.hours.value, { hours: formatHours(value, locale) });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.support.hours.title}</CardTitle>
        <CardDescription>{t.support.hours.scope}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : error || !organization ? (
          <p className="text-sm text-muted-foreground">{t.support.hours.loadFailed}</p>
        ) : (
          <dl className="grid grid-cols-[1fr_auto] gap-y-2 text-sm">
            <dt className="text-muted-foreground">{t.support.hours.available}</dt>
            <dd className="font-semibold">{hours(organization.consultantHoursAvailable)}</dd>
            <dt className="text-muted-foreground">{t.support.hours.spent}</dt>
            <dd className="font-medium">{hours(organization.consultantHoursSpent)}</dd>
            <dt className="text-muted-foreground">{t.support.hours.assigned}</dt>
            <dd className="font-medium">{hours(organization.consultantHoursAssigned)}</dd>
          </dl>
        )}
        {canBuy ? (
          <Button variant="outline" size="sm" onClick={() => { setBuying(true); }}>
            {t.support.hours.buy}
          </Button>
        ) : (
          <p className="text-sm text-muted-foreground">{t.support.hours.askAdmin}</p>
        )}
      </CardContent>
      {canBuy && <BuyHoursDialog orgId={orgId} open={buying} onOpenChange={setBuying} />}
    </Card>
  );
}
